import { useState } from "react";
import { useGetApiRequests, triggerNotification } from "../../../common/CommonExport";
import { Employee, EmployeeKpiData, ApiError } from "../types";

interface UseEditKpiProps {
  monthYear: string;
  onSuccess: () => void;
  reportingManagerId?: number | string;
}

export const useEditKpi = ({ monthYear, onSuccess, reportingManagerId }: UseEditKpiProps) => {
  const [isEditKpiModalOpen, setIsEditKpiModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [viewKpiData, setViewKpiData] = useState<EmployeeKpiData[]>([]);
  const [editKpiInput, setEditKpiInput] = useState<Record<number, { target?: number; actual?: number; weight?: number; kpiId?: number; kpiQuestionaireId?: number }>>({});

  const getEmployeeKpiDetailsApi = useGetApiRequests("employeeKpiDetail", "GET");
  const putKpiUpdateApi = useGetApiRequests("kpiupdatedeatils", "PUT");

  const handleOpenEditKpiModal = async (employee: Employee) => {
    setSelectedEmployee(employee);
    setIsEditKpiModalOpen(true);
    setModalLoading(true);
    try {
      const queryParams = { monthYear: monthYear };
      const pathParams = { id: employee.employeeId };
      const response = await getEmployeeKpiDetailsApi(null, queryParams, pathParams);
      if (response && "data" in response && response.data.status.success === "Success") {
        const data = response.data.data;
        setViewKpiData(data || []);
        const inputMap: Record<number, { target?: number; actual?: number; weight?: number; kpiId?: number; kpiQuestionaireId?: number }> = {};
        data.forEach((item: EmployeeKpiData) => {
          const hasTarget = item.totalTargets != null && item.totalTargets !== 0;
          const hasWeightage = item.kpiQuestionaire?.kpiWeightage != null && item.kpiQuestionaire.kpiWeightage !== 0;
          const actual =
            hasTarget && hasWeightage && item.weightScore != null
              ? (item.weightScore / item.kpiQuestionaire.kpiWeightage) * item.totalTargets
              : undefined;
          inputMap[item.kpiQuestionaire.kpiQuestionaireId] = {
            target: item.totalTargets,
            actual,
            weight: item.weightScore,
            kpiId: item.kpiId,
            kpiQuestionaireId: item.kpiQuestionaire.kpiQuestionaireId,
          };
        });
        setEditKpiInput(inputMap);
      } else {
        setViewKpiData([]);
        const errorMsg = (response as ApiError)?.data?.status?.message || "No KPI data found.";
        triggerNotification("warning", errorMsg, "", "topRight");
      }
    } catch (error) {
      console.error("Failed to fetch Employee KPI Details for Edit", error);
      const err = error as ApiError;
      const errorMessage = err.response?.data?.status?.message || err.response?.data?.message || err.message || "Failed to fetch KPI details.";
      triggerNotification("error", errorMessage, "", "topRight");
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseEditKpiModal = () => {
    setIsEditKpiModalOpen(false);
    setSelectedEmployee(null);
    setViewKpiData([]);
    setEditKpiInput({});
  };

  const handleEditKpiInputChange = (id: number, field: "target" | "weight" | "actual", value: number | null) => {
    let finalValue = value;
    if (field === "target" && finalValue !== null && finalValue > 999) {
      finalValue = 999;
    }

    if (field === "weight" && finalValue !== null) {
      const kpiItem = viewKpiData.find((item) => item.kpiQuestionaire.kpiQuestionaireId === id);
      if (kpiItem && finalValue > kpiItem.kpiQuestionaire.kpiWeightage) {
        return;
      }
    }

    setEditKpiInput((prev) => {
      let finalValue = value;
      const currentInput = prev[id] || {};

      if (field === "actual" && finalValue !== null && currentInput.target !== undefined && finalValue > currentInput.target) {
        finalValue = currentInput.target;
      }

      const next = {
        ...prev,
        [id]: { ...currentInput, [field]: finalValue },
      };

 
      if (field === "target" && finalValue !== null) {
        const currentActual = next[id].actual;
        if (currentActual !== undefined && currentActual > finalValue) {
          next[id].actual = finalValue;
        }
      }

      const kpiItem = viewKpiData.find((item) => item.kpiQuestionaire.kpiQuestionaireId === id);
      const input = next[id];

      if (kpiItem && input?.target != null && input?.actual != null && input.target !== 0) {
        next[id] = {
          ...input,
          weight: (input.actual / input.target) * kpiItem.kpiQuestionaire.kpiWeightage,
        };
      } else if (input) {
        next[id] = { ...input, weight: undefined };
      }

      return next;
    });
  };

  const handleUpdateKpi = async () => {
    if (!selectedEmployee) return;

    const payload = Object.values(editKpiInput).map((input) => ({
      employee: { employeeId: selectedEmployee.employeeId },
      kpiId: input.kpiId,
      totalTargets: input.target,
      weightScore: input.weight,
    }));

    setModalLoading(true);
    try {
      const pathParams = { id: reportingManagerId };
      const response = await putKpiUpdateApi(payload, {}, pathParams);

      if (response && "data" in response && response.data.status.success === "Success") {
        triggerNotification("success", response.data.status.message || "KPI updated successfully!", "", "topRight");
        handleCloseEditKpiModal();
        onSuccess();
      } else {
        const errorMsg = (response as ApiError)?.data?.status?.message || "Failed to update KPI.";
        triggerNotification("error", errorMsg, "", "topRight");
      }
    } catch (error) {
      console.error("Update KPI error", error);
      const err = error as ApiError;
      const errorMessage = err.response?.data?.status?.message || err.response?.data?.message || err.message || "An error occurred while updating KPI.";
      triggerNotification("error", errorMessage, "", "topRight");
    } finally {
      setModalLoading(false);
    }
  };

  return {
    isEditKpiModalOpen,
    selectedEmployeeEdit: selectedEmployee,
    modalLoadingEdit: modalLoading,
    viewKpiDataEdit: viewKpiData,
    handleOpenEditKpiModal,
    handleCloseEditKpiModal,
    editKpiInput,
    handleEditKpiInputChange,
    handleUpdateKpi,
  };
};
